import { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle, ShieldCheck, TrendingUp, Download, Lock, BadgeCheck, Loader2,
  FileText, ListChecks, Calculator, XCircle, ArrowRight, Sparkles, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SubstackHeaderButton, SubstackSlimFooter } from "@/components/SubstackLink";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/96f08e1b-7980-446d-91e9-995f5c811979/images/f13b0c41953ed7cbbb3524828b70dfade5701a9cf0a7c155556a069c41c9b1fd.jpeg";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.7 }, viewport: { once: true } };

const RidgemontLogo = ({ className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 12 L88 82 L12 82 Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
    <path d="M28 62 L72 62" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const scrollToForm = () => {
  if (window.dataLayer) window.dataLayer.push({ event: 'workbook_free_claim_click' });
  document.getElementById('claim-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const ClaimButton = ({ testId, className = "" }) => (
  <Button onClick={scrollToForm} className={`btn-accent text-base md:text-lg px-8 py-6 md:py-7 font-semibold ${className}`} data-testid={testId}>
    Get the Free Retirement Workbook Now
    <ArrowRight className="w-5 h-5 ml-2" />
  </Button>
);

const WorkbookPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads/workbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (window.dataLayer) window.dataLayer.push({ event: 'workbook_free_lead' });
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: 'Something went wrong. Please try again or email hello@ridgemontfinancial.com.' });
    } finally {
      setSubmitting(false);
    }
  };

  const deliverables = [
    {
      icon: FileText,
      name: "The Printable & Digital Implementation Workbook",
      desc: "Step-by-step, fill-in-the-blank exercises to map out your non-negotiable income baseline — completable in one evening.",
      value: "$249 value"
    },
    {
      icon: TrendingUp,
      name: "Retirement Analyzer Software Walkthrough Guide",
      desc: "Easy-to-follow instructions to run real-world market crash scenarios against your specific portfolio using the Retirement Analyzer methodology.",
      value: "$199 value"
    },
    {
      icon: ListChecks,
      name: "Annuity Fee-Buster & Carrier Checklist",
      desc: "Quickly spot hidden surrender charges, rider fees, and administrative costs before you sign anything.",
      value: "$149 value"
    },
    {
      icon: Calculator,
      name: "Social Security Optimization Worksheet",
      desc: "Calculate your peak payout age to maximize guaranteed monthly income for you and your spouse.",
      value: "$129 value"
    }
  ];

  return (
    <div className="bg-white" data-testid="workbook-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="logo-home">
            <RidgemontLogo className="w-8 h-8 text-[#0A192F]" />
            <div className="flex flex-col leading-none">
              <span className="text-xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ridgemont</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mt-0.5">Financial</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <SubstackHeaderButton />
            <Button onClick={scrollToForm} className="btn-accent px-5 py-2 md:px-6 md:py-5 text-sm md:text-base" data-testid="nav-buy-cta">
              Get the Workbook — Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0A192F] text-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/15 border border-[#C5A059]/40 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              <span className="text-sm text-[#C5A059] font-medium tracking-wide">Normally $97 · Free for a Limited Time</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="hero-headline">
              Build Your Guaranteed Retirement Income Floor — <span className="text-[#C5A059] font-medium">Free</span> <span className="text-slate-400 text-3xl sm:text-4xl align-middle line-through">$97</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl" data-testid="hero-subheadline">
              The step-by-step implementation workbook and Retirement Analyzer workflow to stress-test your portfolio, eliminate sequence-of-returns risk, and lock in lifetime income.
            </p>
            <ClaimButton testId="hero-buy-cta" />
            <div className="flex items-center gap-2 mt-6 text-sm text-slate-400">
              <BadgeCheck className="w-4 h-4 text-[#2DD4BF]" />
              Powered by the Retirement Analyzer software methodology used by top financial transition specialists
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <img src={HERO_IMG} alt="Retirement & Annuities Implementation Workbook with Retirement Analyzer dashboard" className="rounded-xl shadow-2xl border border-white/10 w-full" data-testid="hero-product-image" />
          </motion.div>
        </div>
      </section>

      {/* Section 1: Problem */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] mb-3">The Problem</p>
            <h2 className="text-base md:text-lg font-semibold text-[#0A192F] uppercase tracking-wide mb-2">Stop Paying $3,000+ for a Financial Plan You Can Build Yourself</h2>
            <div className="text-3xl md:text-4xl font-light text-[#0A192F] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Most advisors just plug your numbers into standard software — and charge you thousands for the printout.
            </div>
            <p className="text-base text-slate-600 leading-relaxed max-w-3xl mb-4">
              Most pre-retirees are told they need to pay $2,000 to $5,000 for a formal financial plan, or surrender 1% of their entire nest egg every single year to an advisor.
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-3xl">
              If the stock market drops during your first few years of retirement, static withdrawal models fail to protect your capital. You don't need an expensive advisor to stress-test your retirement — you need the right step-by-step framework.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="border border-red-200 bg-red-50/50 rounded-xl p-8" data-testid="comparison-advisor">
              <XCircle className="w-8 h-8 text-red-400 mb-4" />
              <div className="text-4xl font-light text-red-500 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>$3,000+</div>
              <p className="font-semibold text-[#0A192F] mb-2">Typical Advisor Financial Plan</p>
              <p className="text-sm text-slate-600">Plus 1% of your nest egg, every year, forever — often for a standard software printout.</p>
            </div>
            <div className="border border-[#C5A059]/40 bg-[#FDF9F0] rounded-xl p-8" data-testid="comparison-workbook">
              <CheckCircle className="w-8 h-8 text-[#C5A059] mb-4" />
              <div className="text-4xl font-light text-[#0A192F] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Free <span className="text-xl text-slate-400 line-through">$97</span></div>
              <p className="font-semibold text-[#0A192F] mb-2">Self-Directed Retirement Workbook</p>
              <p className="text-sm text-slate-600">The same stress-testing framework, in plug-and-play form you complete in one evening — free for a limited time.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Mechanism */}
      <section className="py-16 md:py-24 bg-[#F8F7F4]">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] mb-3">The Mechanism</p>
            <h2 className="text-base md:text-lg font-semibold text-[#0A192F] uppercase tracking-wide mb-2">Your Fill-in-the-Blank Blueprint to Outlive-Proof Your Money</h2>
            <div className="text-3xl md:text-4xl font-light text-[#0A192F] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Complex software modeling, translated into simple exercises you complete in one evening.
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Identify Your Baseline", desc: "Pin down your non-negotiable living expenses — the income floor that must be covered no matter what the market does." },
              { step: "02", title: "Stress-Test the Risk", desc: "Run sequence-of-returns scenarios against your portfolio using the proven Retirement Analyzer methodology." },
              { step: "03", title: "Lock In the Floor", desc: "Select downside-protected fixed annuities to cover your income gap for life — while keeping liquid assets under your control." }
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-xl p-8 border border-[#E7E5E4]" data-testid={`mechanism-step-${s.step}`}>
                <div className="text-[#C5A059] text-sm tracking-[0.2em] mb-3">STEP {s.step}</div>
                <h3 className="text-xl font-medium text-[#0A192F] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Section 3: What's Included */}
      <section className="py-16 md:py-24 bg-[#0A192F] text-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] mb-3">What's Included</p>
            <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Everything You Get — Free Today</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {deliverables.map((d, i) => (
              <motion.div {...fadeUp} key={d.name} className="bg-white/5 border border-white/10 rounded-xl p-8 flex gap-5" data-testid={`deliverable-${i}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center">
                  <d.icon className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">{d.name}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed mb-3">{d.desc}</p>
                  <span className="text-xs tracking-wide uppercase text-[#2DD4BF]">{d.value}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="text-center mt-12">
            <p className="text-slate-300 mb-2">Total value: <span className="line-through text-slate-400">$726</span> · Normally <span className="line-through text-slate-400">$97</span></p>
            <p className="text-3xl font-light mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Today: <span className="text-[#C5A059] font-medium">FREE</span></p>
            <ClaimButton testId="included-buy-cta" />
          </motion.div>
        </div>
      </section>

      {/* Section 4: Objections */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] mb-3">The Honest Question</p>
            <div className="text-3xl md:text-4xl font-light text-[#0A192F] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Is a Free Workbook Really Enough to Secure Your Retirement?
            </div>
            <p className="text-base text-slate-600 leading-relaxed max-w-3xl mb-4">
              Yes — because it eliminates filler theory and focuses strictly on execution. You don't need a 300-page textbook; you need a plug-and-play system that shows you where your money goes and how to lock in guaranteed income floors.
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-3xl">
              You keep 100% control of your liquid assets while setting up downside protection for your core expenses.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="border border-[#E7E5E4] rounded-xl p-8" data-testid="contrast-generic">
              <p className="font-semibold text-slate-400 mb-4 uppercase text-sm tracking-wide">Generic 300-Page Financial Book</p>
              {["Abstract theory you never apply", "No worksheets or calculators", "Doesn't touch your actual numbers", "Sits on your shelf unfinished"].map(item => (
                <div key={item} className="flex items-center gap-3 py-2 text-sm text-slate-500">
                  <XCircle className="w-4 h-4 text-red-300 flex-shrink-0" /> {item}
                </div>
              ))}
            </div>
            <div className="border border-[#C5A059]/40 bg-[#FDF9F0] rounded-xl p-8" data-testid="contrast-workbook">
              <p className="font-semibold text-[#0A192F] mb-4 uppercase text-sm tracking-wide">Actionable Implementation Workbook</p>
              {["Fill-in-the-blank execution exercises", "Income gap calculators & flowcharts", "Stress-tests YOUR portfolio", "Done in one evening"].map(item => (
                <div key={item} className="flex items-center gap-3 py-2 text-sm text-[#0A192F]">
                  <CheckCircle className="w-4 h-4 text-[#C5A059] flex-shrink-0" /> {item}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 5: Final Offer / Claim Form */}
      <section className="py-16 md:py-24 bg-[#F8F7F4]" id="claim-form">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="bg-white border-2 border-[#C5A059]/50 rounded-2xl p-8 md:p-12 text-center shadow-xl" data-testid="final-offer-box">
            {submitted ? (
              <div data-testid="claim-success">
                <div className="w-16 h-16 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/40 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-[#2DD4BF]" />
                </div>
                <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Your Free Workbook Is On the Way
                </h2>
                <p className="text-base text-slate-600 leading-relaxed max-w-xl mx-auto">
                  Check your inbox — we're sending the Retirement & Annuities Implementation Workbook to <span className="font-medium text-[#0A192F]">{formData.email}</span>. If you don't see it within a few minutes, check your spam folder.
                </p>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 bg-[#0A192F] text-white rounded-full px-4 py-1.5 mb-6 text-sm">
                  <Download className="w-4 h-4 text-[#C5A059]" /> Free Instant Access · Normally $97
                </div>
                <h2 className="text-3xl md:text-4xl font-light text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Claim Your Free Retirement Workbook Before the Next Market Shift
                </h2>
                <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-xl mx-auto">
                  Protect your hard-earned savings today with the exact workbook and software framework used by retirement pros. Tell us where to send it — free for a limited time.
                </p>
                <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4 text-left">
                  <div>
                    <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" className="py-6 text-base" data-testid="claim-name-input" />
                    {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="claim-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Your best email address" className="py-6 text-base" data-testid="claim-email-input" />
                    {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="claim-email-error">{errors.email}</p>}
                  </div>
                  <Button type="submit" disabled={submitting} className="btn-accent w-full text-base md:text-lg py-6 md:py-7 font-semibold" data-testid="claim-submit-btn">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Me the Free Workbook <ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                  {errors.submit && <p className="text-red-500 text-sm text-center" data-testid="claim-submit-error">{errors.submit}</p>}
                </form>
                <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#2DD4BF]" /> Your Info Stays Private</span>
                  <span className="flex items-center gap-1.5"><Download className="w-4 h-4 text-[#2DD4BF]" /> Instant Access</span>
                  <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#2DD4BF]" /> No Obligation</span>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Disclaimer + Footer */}
      <section className="bg-white border-t border-[#E7E5E4] py-10">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <p className="text-xs text-slate-400 leading-relaxed max-w-4xl" data-testid="legal-disclaimer">
            Disclaimer: The Retirement & Annuities Implementation Workbook is educational material only and does not constitute formal fiduciary, tax, or legal advice. Annuity guarantees are backed by the claims-paying ability of the issuing insurance carrier. Product availability, features, caps, and participation rates vary by state and carrier. Consult a licensed professional regarding your individual situation before making financial decisions. Ridgemont Financial is not affiliated with Retirement Analyzer; references describe methodology used in our planning process.
          </p>
        </div>
      </section>
      <SubstackSlimFooter />
    </div>
  );
};

export default WorkbookPage;
